import {
    Box,
    Button,
    Tab,
    Tabs,
    TextField,
    InputAdornment,
    Typography,
    Checkbox,
    CircularProgress,
    Chip
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PopupForm from "../../../Components/PopupForm";
import Cookies from "universal-cookie";
import userAPI from "../../../Network/User";
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const cookies = new Cookies();

// Footer component for save/close buttons
const RenderFooter = (props) => {
    const [t] = useTranslation("common");
    const gridtheme = useSelector((state) => state.themeData.gridtheme);
    const maintheme = useSelector((state) => state.themeData.maintheme);

    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ display: "flex", justifyContent: "end", alignItems: "center", width: "100%" }}>
                <Box display="flex">
                    <Button
                        variant="outlined"
                        sx={{
                            width: "50% !important",
                            minWidth: "80px !important",
                            maxWidth: "80px !important",
                            margin: 1,
                            backgroundColor: "#f7f7f7",
                            borderColor: gridtheme?.colorWhite,
                            color: gridtheme?.colorblack,
                            boxShadow: "0 0 7px -2px white",
                            "&:hover": {
                                backgroundColor: "#f7f7f7",
                                borderColor: gridtheme?.colorWhite,
                                color: gridtheme?.colorblack,
                            },
                            height: "35px",
                            fontFamily: "Cairo-Bold",
                        }}
                        onClick={() => props?.setOpen(false)}
                    >
                        {t("GLOBAL.close")}
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: maintheme?.secondary,
                            margin: 1,
                            width: "80px !important",
                            minWidth: "80px !important",
                            maxWidth: "80px !important",
                            height: "35px",
                            fontFamily: "Cairo-Bold",
                        }}
                        onClick={() => props?.submit()}
                        disabled={props?.loading}
                    >
                        {props?.loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : t("GLOBAL.Save")}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// Tab Panel component
function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index} style={{ height: '100%' }}>
            {value === index && <Box sx={{ p: 2, height: '100%' }}>{children}</Box>}
        </div>
    );
}

export default function UserAccountsTree(props) {
    const [t] = useTranslation("common");
    const maintheme = useSelector((state) => state.themeData.maintheme);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [searchText, setSearchText] = useState("");

    // Tree data state
    const [municipalities, setMunicipalities] = useState([]);
    const [contractors, setContractors] = useState([]);
    const [accounts, setAccounts] = useState([]);

    // Selected items - track at parent level
    const [selectedMunicipalities, setSelectedMunicipalities] = useState([]);
    const [selectedContractors, setSelectedContractors] = useState([]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);

    // Filter state
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    // Calculate total accounts selected (sum of account_count from municipalities + contractors + direct accounts)
    const totalAccountsSelected = useMemo(() => {
        // Get account count from selected municipalities
        const municipalityAccounts = municipalities
            .filter(m => selectedMunicipalities.includes(m.municipalityId))
            .reduce((sum, m) => sum + (m.account_count || 0), 0);

        // Get account count from selected contractors
        const contractorAccounts = contractors
            .filter(c => selectedContractors.includes(c.contractorId))
            .reduce((sum, c) => sum + (c.account_count || 0), 0);

        // Add directly selected accounts
        return municipalityAccounts + contractorAccounts + selectedAccounts.length;
    }, [municipalities, contractors, selectedMunicipalities, selectedContractors, selectedAccounts]);

    // Calculate account IDs that are included via selected municipalities or contractors
    // Store as Map with { accountId: { type: 'municipality'|'contractor', name: string } }
    const accountsFromParents = useMemo(() => {
        const accountMap = new Map();

        // Get account IDs from selected municipalities
        municipalities
            .filter(m => selectedMunicipalities.includes(m.municipalityId))
            .forEach(m => {
                m.children?.forEach(child => {
                    const accountId = child.accountId || child.id;
                    if (!accountMap.has(accountId)) {
                        accountMap.set(accountId, { type: 'municipality', name: m.name });
                    }
                });
            });

        // Get account IDs from selected contractors
        contractors
            .filter(c => selectedContractors.includes(c.contractorId))
            .forEach(c => {
                c.children?.forEach(child => {
                    const accountId = child.accountId || child.id;
                    if (!accountMap.has(accountId)) {
                        accountMap.set(accountId, { type: 'contractor', name: c.name });
                    }
                });
            });

        return accountMap;
    }, [municipalities, contractors, selectedMunicipalities, selectedContractors]);

    const headerObject = {
        username: cookies.get("user_name") ? cookies.get("user_name") : null,
        user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
        account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
        account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
        authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
        sid: cookies.get("sid") ? cookies.get("sid") : null,
        baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
        id: cookies.get("id") ? cookies.get("id") : null,
    };

    // Load tree data
    useEffect(() => {
        if (props?.openForm) {
            loadTreeData();
        }
    }, [props?.openForm, props?.object]);

    const loadTreeData = async () => {
        setLoading(true);
        try {
            const result = await userAPI.getAccountsTree({
                params: { ...headerObject, target_user_id: props?.object?.id }
            });

            if (result?.status && result?.data?.data) {
                setMunicipalities(result.data.data.municipalities || []);
                setContractors(result.data.data.contractors || []);
                setAccounts(result.data.data.accounts || []);
                // Load user's current selections from the API response
                setSelectedMunicipalities(result.data.data.userMunicipalityIds || []);
                setSelectedContractors(result.data.data.userContractorIds || []);
                setSelectedAccounts(result.data.data.userAccountIds || []);
            }
        } catch (error) {
            console.error("Error loading accounts tree:", error);
            toast.error(t("NETWORK_MESSAGES.unknownError"));
        }
        setLoading(false);
    };

    // Submit handler
    const submit = async () => {
        if (!props?.object?.id) {
            toast.warn(t('USERS.please_select_a_user'));
            return;
        }

        setSaving(true);
        try {
            const result = await userAPI.updateAccessibleAccounts({
                id: props.object.id,
                data: {
                    municipality_ids: selectedMunicipalities,
                    contractor_ids: selectedContractors,
                    account_ids: selectedAccounts
                },
                params: { ...headerObject }
            });

            if (result?.status && result?.data?.status) {
                toast.success(result?.data?.data?.message?.text || t("GLOBAL.update_success"));
                props?.loadData();
                props?.setOpenForm(false);
            } else {
                toast.error(result?.data?.data?.message?.text || t("NETWORK_MESSAGES.unknownError"));
            }
        } catch (error) {
            toast.error(error.message);
        }
        setSaving(false);
    };

    // Toggle account selection
    const toggleAccount = (accountId) => {
        setSelectedAccounts(prev => {
            if (prev.includes(accountId)) {
                return prev.filter(id => id !== accountId);
            }
            return [...prev, accountId];
        });
    };

    // Toggle municipality selection at the parent level
    const toggleMunicipality = (municipality) => {
        const municipalityId = municipality.municipalityId;
        setSelectedMunicipalities(prev => {
            if (prev.includes(municipalityId)) {
                return prev.filter(id => id !== municipalityId);
            }
            return [...prev, municipalityId];
        });
    };

    // Toggle contractor selection at the parent level
    const toggleContractor = (contractor) => {
        const contractorId = contractor.contractorId;
        setSelectedContractors(prev => {
            if (prev.includes(contractorId)) {
                return prev.filter(id => id !== contractorId);
            }
            return [...prev, contractorId];
        });
    };

    // Legacy toggle for direct account selection in accounts tab
    const toggleParent = (parent) => {
        const childIds = parent.children?.map(c => c.id) || [];
        const allSelected = childIds.every(id => selectedAccounts.includes(id));

        setSelectedAccounts(prev => {
            if (allSelected) {
                // Deselect all children
                return prev.filter(id => !childIds.includes(id));
            } else {
                // Select all children
                const newSelected = [...prev];
                childIds.forEach(id => {
                    if (!newSelected.includes(id)) {
                        newSelected.push(id);
                    }
                });
                return newSelected;
            }
        });
    };

    // Filter data based on search
    const filteredMunicipalities = useMemo(() => {
        if (!searchText) return municipalities;
        const search = searchText.toLowerCase();
        return municipalities.filter(m =>
            m.name?.toLowerCase().includes(search) ||
            m.children?.some(c => c.name?.toLowerCase().includes(search))
        ).map(m => ({
            ...m,
            children: m.children?.filter(c =>
                c.name?.toLowerCase().includes(search) ||
                m.name?.toLowerCase().includes(search)
            )
        }));
    }, [municipalities, searchText]);

    // Apply showSelectedOnly filter to municipalities
    const displayMunicipalities = useMemo(() => {
        if (!showSelectedOnly) return filteredMunicipalities;
        return filteredMunicipalities.filter(m => selectedMunicipalities.includes(m.municipalityId));
    }, [filteredMunicipalities, showSelectedOnly, selectedMunicipalities]);

    const filteredContractors = useMemo(() => {
        if (!searchText) return contractors;
        const search = searchText.toLowerCase();
        return contractors.filter(c =>
            c.name?.toLowerCase().includes(search) ||
            c.children?.some(child => child.name?.toLowerCase().includes(search))
        ).map(c => ({
            ...c,
            children: c.children?.filter(child =>
                child.name?.toLowerCase().includes(search) ||
                c.name?.toLowerCase().includes(search)
            )
        }));
    }, [contractors, searchText]);

    // Apply showSelectedOnly filter to contractors
    const displayContractors = useMemo(() => {
        if (!showSelectedOnly) return filteredContractors;
        return filteredContractors.filter(c => selectedContractors.includes(c.contractorId));
    }, [filteredContractors, showSelectedOnly, selectedContractors]);

    const filteredAccounts = useMemo(() => {
        if (!searchText) return accounts;
        const search = searchText.toLowerCase();
        return accounts.filter(a => a.name?.toLowerCase().includes(search));
    }, [accounts, searchText]);

    // Apply showSelectedOnly filter to accounts (include both directly selected AND from parents)
    const displayAccounts = useMemo(() => {
        if (!showSelectedOnly) return filteredAccounts;
        return filteredAccounts.filter(a => selectedAccounts.includes(a.id) || accountsFromParents.has(a.id));
    }, [filteredAccounts, showSelectedOnly, selectedAccounts, accountsFromParents]);

    // Render tree item for municipality
    const renderMunicipalityTreeItem = (node) => {
        const isSelected = selectedMunicipalities.includes(node.municipalityId);

        return (
            <TreeItem
                key={node.id}
                nodeId={node.id}
                label={
                    <Box sx={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', py: 0.5, width: '100%' }}>
                        <Checkbox
                            checked={isSelected}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleMunicipality(node);
                            }}
                            sx={{ ml: 1 }}
                        />
                        <Typography sx={{ fontFamily: 'Cairo', flex: 1, textAlign: 'left' }}>
                            {node.name}
                        </Typography>
                        <Chip
                            label={`${node.account_count || 0} حساب`}
                            size="small"
                            sx={{
                                mr: 1,
                                backgroundColor: isSelected ? maintheme?.secondary : '#e0e0e0',
                                color: isSelected ? '#fff' : '#333',
                                fontFamily: 'Cairo'
                            }}
                        />
                    </Box>
                }
            >
                {node.children?.map(child => (
                    <TreeItem
                        key={child.id}
                        nodeId={child.id}
                        label={
                            <Box sx={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', py: 0.3 }}>
                                <Typography sx={{ fontFamily: 'Cairo', fontSize: '14px', textAlign: 'left', flex: 1, color: '#666' }}>
                                    {child.name}
                                </Typography>
                            </Box>
                        }
                    />
                ))}
            </TreeItem>
        );
    };

    // Render tree item for contractor
    const renderContractorTreeItem = (node) => {
        const isSelected = selectedContractors.includes(node.contractorId);

        return (
            <TreeItem
                key={node.id}
                nodeId={node.id}
                label={
                    <Box sx={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', py: 0.5, width: '100%' }}>
                        <Checkbox
                            checked={isSelected}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleContractor(node);
                            }}
                            sx={{ ml: 1 }}
                        />
                        <Typography sx={{ fontFamily: 'Cairo', flex: 1, textAlign: 'left' }}>
                            {node.name}
                        </Typography>
                        <Chip
                            label={`${node.account_count || 0} حساب`}
                            size="small"
                            sx={{
                                mr: 1,
                                backgroundColor: isSelected ? maintheme?.secondary : '#e0e0e0',
                                color: isSelected ? '#fff' : '#333',
                                fontFamily: 'Cairo'
                            }}
                        />
                    </Box>
                }
            >
                {node.children?.map(child => (
                    <TreeItem
                        key={child.id}
                        nodeId={child.id}
                        label={
                            <Box sx={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', py: 0.3 }}>
                                <Typography sx={{ fontFamily: 'Cairo', fontSize: '14px', textAlign: 'left', flex: 1, color: '#666' }}>
                                    {child.name}
                                </Typography>
                            </Box>
                        }
                    />
                ))}
            </TreeItem>
        );
    };

    // Render account list item
    const renderAccountItem = (account) => {
        const parentInfo = accountsFromParents.get(account.id);
        const isFromParent = !!parentInfo;
        const isSelected = selectedAccounts.includes(account.id) || isFromParent;

        // Build the label for accounts included via parent
        let parentLabel = "";
        if (parentInfo) {
            if (parentInfo.type === 'municipality') {
                parentLabel = `${t("GLOBAL.included_via_municipality")} ${parentInfo.name}`;
            } else {
                parentLabel = `${t("GLOBAL.included_via_contractor")} ${parentInfo.name}`;
            }
        }

        return (
            <Box
                key={account.id}
                sx={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    alignItems: 'center',
                    py: 0.5,
                    px: 1,
                    borderBottom: '1px solid #f0f0f0',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    opacity: isFromParent ? 0.7 : 1,
                    backgroundColor: isFromParent ? '#f9f9f9' : 'transparent'
                }}
            >
                <Checkbox
                    checked={isSelected}
                    onChange={() => !isFromParent && toggleAccount(account.id)}
                    disabled={isFromParent}
                    sx={{ ml: 1 }}
                />
                <Typography sx={{ fontFamily: 'Cairo', flex: 1, textAlign: 'left', color: isFromParent ? '#888' : 'inherit' }}>
                    {account.name}
                </Typography>
                {isFromParent && (
                    <Chip
                        label={parentLabel}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 1, fontSize: '10px', height: '20px', fontFamily: 'Cairo' }}
                    />
                )}
            </Box>
        );
    };

    return (
        <PopupForm
            isFullScreen={true}
            open={props?.openForm}
            setOpen={props?.setOpenForm}
            customWidth="700px"
            customeHeight="600px"
            title={t("USERS.user_accounts")}
            content={
                <div dir="rtl">
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '450px' }}>
                        {/* Tabs */}
                        <Tabs
                            value={tabValue}
                            onChange={(e, newValue) => {
                                setTabValue(newValue);
                                setSearchText("");
                            }}
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                                '& .MuiTab-root': { fontFamily: 'Cairo' }
                            }}
                        >
                            <Tab
                                icon={<AccountBalanceIcon />}
                                iconPosition="start"
                                label={`${t("USERS.municipalities")} (${municipalities.length})`}
                            />
                            <Tab
                                icon={<BusinessIcon />}
                                iconPosition="start"
                                label={`${t("USERS.contractors")} (${contractors.length})`}
                            />
                            <Tab
                                icon={<AccountCircleIcon />}
                                iconPosition="start"
                                label={`${t("USERS.accounts")} (${accounts.length})`}
                            />
                        </Tabs>

                        {/* Search field */}
                        <TextField
                            size="small"
                            placeholder={t("GLOBAL.search")}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            sx={{
                                m: 2,
                                mb: 1,
                                '& .MuiInputBase-input': { fontFamily: 'Cairo' }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Selected count and filter toggle */}
                        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip
                                label={`${t("GLOBAL.accounts_selected")}: ${totalAccountsSelected}`}
                                color="primary"
                                size="small"
                                sx={{ fontFamily: 'Cairo' }}
                            />
                            <Chip
                                label={showSelectedOnly ? t("GLOBAL.show_all") : t("GLOBAL.show_selected_only")}
                                onClick={() => setShowSelectedOnly(!showSelectedOnly)}
                                variant={showSelectedOnly ? "filled" : "outlined"}
                                color={showSelectedOnly ? "secondary" : "default"}
                                size="small"
                                sx={{ fontFamily: 'Cairo', cursor: 'pointer' }}
                            />
                        </Box>

                        {/* Content */}
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, overflow: 'auto' }}>
                                {/* Municipalities Tab */}
                                <TabPanel value={tabValue} index={0}>
                                    <TreeView
                                        defaultCollapseIcon={<ExpandMoreIcon />}
                                        defaultExpandIcon={<ChevronLeftIcon />}
                                        sx={{ direction: 'rtl' }}
                                    >
                                        {displayMunicipalities.map(m => renderMunicipalityTreeItem(m))}
                                    </TreeView>
                                    {displayMunicipalities.length === 0 && (
                                        <Typography sx={{ textAlign: 'center', py: 4, fontFamily: 'Cairo', color: '#999' }}>
                                            {t("GLOBAL.no_data")}
                                        </Typography>
                                    )}
                                </TabPanel>

                                {/* Contractors Tab */}
                                <TabPanel value={tabValue} index={1}>
                                    <TreeView
                                        defaultCollapseIcon={<ExpandMoreIcon />}
                                        defaultExpandIcon={<ChevronLeftIcon />}
                                        sx={{ direction: 'rtl' }}
                                    >
                                        {displayContractors.map(c => renderContractorTreeItem(c))}
                                    </TreeView>
                                    {displayContractors.length === 0 && (
                                        <Typography sx={{ textAlign: 'center', py: 4, fontFamily: 'Cairo', color: '#999' }}>
                                            {t("GLOBAL.no_data")}
                                        </Typography>
                                    )}
                                </TabPanel>

                                {/* Accounts Tab */}
                                <TabPanel value={tabValue} index={2}>
                                    <Box sx={{ direction: 'rtl' }}>
                                        {displayAccounts.map(a => renderAccountItem(a))}
                                        {displayAccounts.length === 0 && (
                                            <Typography sx={{ textAlign: 'center', py: 4, fontFamily: 'Cairo', color: '#999' }}>
                                                {t("GLOBAL.no_data")}
                                            </Typography>
                                        )}
                                    </Box>
                                </TabPanel>
                            </Box>
                        )}
                    </Box>
                </div>
            }
            footer={
                <RenderFooter
                    open={props?.openForm}
                    setOpen={props?.setOpenForm}
                    submit={submit}
                    loading={saving}
                />
            }
        />
    );
}
